import { useEffect, useState } from "react";
import logger from "@/lib/logger";

export type AccountEntitlement = "ADMIN" | "INVITEE" | "PUBLIC" | null;

type EntitlementState = {
  entitlement: AccountEntitlement;
  loading: boolean;
};

const PENDING: EntitlementState = { entitlement: null, loading: true };

/**
 * Fetches the caller's account entitlement from GET /api/auth/me once
 * authenticated (pass enabled=false until auth resolves). Fails secure:
 * any fetch error resolves to entitlement null (the invite-only screen),
 * mirroring the kernel guard's fail-secure lookup. Kept separate from
 * use-auth-state (authentication-only) so the concerns don't tangle.
 */
export function useEntitlement(enabled: boolean): EntitlementState {
  const [state, setState] = useState<EntitlementState>(PENDING);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let mounted = true;
    fetch("/api/auth/me")
      .then((res) => {
        if (!res.ok) {
          throw new Error(`auth/me responded ${res.status}`);
        }
        return res.json();
      })
      .then((body: { entitlement?: AccountEntitlement }) => {
        if (mounted) {
          setState({ entitlement: body.entitlement ?? null, loading: false });
        }
      })
      .catch((error) => {
        logger.error({ err: error }, "[Entitlement] auth/me fetch failed");
        if (mounted) {
          setState({ entitlement: null, loading: false });
        }
      });
    return () => {
      mounted = false;
    };
  }, [enabled]);

  return enabled ? state : PENDING;
}
